import { Injectable, signal } from '@angular/core';
import { catchError, Observable, tap, throwError } from 'rxjs';
import {
  ChatAttachment,
  ChatConversationDetail,
  ChatDiagnostics,
  ChatLeaveResult,
  ChatManualStatus,
  ChatMessage,
  ChatMessagePage,
  ChatPerson,
  ChatPresenceEvent,
  ChatPresenceResult,
  ChatReactionUpdate,
  ChatReadResult,
  ChatSidebarData,
  ChatSyncResult,
} from '../models/chat.models';
import { isChatApiUnavailable } from '../utils/chat-error.util';
import { ChatApiService } from './chat-api.service';

/**
 * The single data source components and {@link ChatStore} talk to.
 *
 * Every call goes to {@link ChatApiService}. There is no fallback data source: this used to swap in
 * a localStorage-backed mock when the API was unreachable, which meant a dead backend produced
 * fabricated conversations seeded with real colleague names — indistinguishable from the real thing
 * unless you noticed a small chip in the sidebar (and that chip was hidden in the floating widget
 * entirely). Fake history is worse than no history, so the session now says so instead:
 * {@link unavailable} drives a "Please refresh your browser" state.
 *
 * What it deliberately does *not* do is treat a real error as an absence. The backend is live and
 * every 4xx carries an `error_code` (§12); those propagate to {@link ChatStore}, which knows what
 * each one means. Only {@link isChatApiUnavailable} — "there is no chat API answering here" —
 * raises the banner, and even then the error is rethrown so the caller still handles it.
 *
 * The decision is sticky in both directions: once a chat call has succeeded, a later blip is a blip
 * rather than a missing backend, and once the banner is up it stays up for the session.
 */
@Injectable({ providedIn: 'root' })
export class ChatBackendService {
  /** True once we've given up on the real API for this session. */
  readonly unavailable = signal(false);

  /** Set by the first successful chat response — after that the API exists, whatever it answers. */
  private backendProven = false;

  constructor(private readonly api: ChatApiService) {}

  /**
   * Pass the call through, noting whether the API is answering at all.
   *
   * Errors are always rethrown — the caller decides what a failure means for its own screen. The
   * only thing this adds is the session-wide "no backend here" flag.
   */
  private route<T>(real: () => Observable<T>): Observable<T> {
    return real().pipe(
      tap(value => {
        // `ApiService` returns EMPTY (completes without emitting) when a request is skipped —
        // an undefined payload means the endpoint answered with nothing usable.
        if (value === undefined || value === null) {
          this.markUnavailable('empty response');
          return;
        }
        this.backendProven = true;
      }),
      catchError(error => {
        if (!this.backendProven && isChatApiUnavailable(error)) {
          this.markUnavailable(error);
        }
        return throwError(() => error);
      })
    );
  }

  private markUnavailable(reason: unknown): void {
    if (this.unavailable() || this.backendProven) {
      return;
    }
    this.unavailable.set(true);
    console.warn('[chat] Backend unavailable — asking the user to refresh.', reason);
  }

  // ---- Sidebar & directory ---------------------------------------------------

  getSidebar(): Observable<ChatSidebarData> {
    return this.route(() => this.api.getSidebar());
  }

  sync(since?: string): Observable<ChatSyncResult> {
    return this.route(() => this.api.sync(since));
  }

  /**
   * Deliberately not routed: the whole value of this call is that it reports on the **real**
   * server, so a session that has already given up on the API has nothing to measure.
   */
  getDiagnostics(): Observable<ChatDiagnostics> {
    if (this.unavailable()) {
      return throwError(() => new Error('Chat backend unavailable — there is nothing to diagnose.'));
    }
    return this.api.getDiagnostics();
  }

  getUsers(search = ''): Observable<ChatPerson[]> {
    return this.route(() => this.api.getUsers(search));
  }

  search(term: string): Observable<{ people: ChatPerson[]; groups: any[] }> {
    return this.route(() => this.api.search(term));
  }

  setUserHidden(userId: string, hidden: boolean): Observable<ChatPerson> {
    return this.route(() => this.api.setUserHidden(userId, hidden));
  }

  // ---- Conversations ---------------------------------------------------------

  openDirect(userId: string): Observable<{ conversation_id: string; created: boolean }> {
    return this.route(() => this.api.openDirect(userId));
  }

  getConversation(conversationId: string): Observable<ChatConversationDetail> {
    return this.route(() => this.api.getConversation(conversationId));
  }

  // ---- Messages --------------------------------------------------------------

  getMessages(conversationId: string, options: { limit?: number; before?: string; after?: string } = {}): Observable<ChatMessagePage> {
    return this.route(() => this.api.getMessages(conversationId, options));
  }

  sendMessage(conversationId: string, clientMsgId: string, body: string, attachmentIds: string[] = []): Observable<ChatMessage> {
    return this.route(() => this.api.sendMessage(conversationId, clientMsgId, body, attachmentIds));
  }

  markRead(conversationId: string, lastReadMessageId: string): Observable<ChatReadResult> {
    return this.route(() => this.api.markRead(conversationId, lastReadMessageId));
  }

  editMessage(messageId: string, body: string): Observable<ChatMessage> {
    return this.route(() => this.api.editMessage(messageId, body));
  }

  deleteMessage(messageId: string): Observable<unknown> {
    return this.route(() => this.api.deleteMessage(messageId));
  }

  addReaction(messageId: string, emoji: string): Observable<ChatReactionUpdate> {
    return this.route(() => this.api.addReaction(messageId, emoji));
  }

  removeReaction(messageId: string, emoji: string): Observable<ChatReactionUpdate> {
    return this.route(() => this.api.removeReaction(messageId, emoji));
  }

  // ---- Groups ----------------------------------------------------------------

  createGroup(name: string, memberIds: string[]): Observable<ChatConversationDetail> {
    return this.route(() => this.api.createGroup(name, memberIds));
  }

  renameGroup(conversationId: string, name: string): Observable<ChatConversationDetail> {
    return this.route(() => this.api.renameGroup(conversationId, name));
  }

  addMembers(conversationId: string, userIds: string[]): Observable<ChatConversationDetail> {
    return this.route(() => this.api.addMembers(conversationId, userIds));
  }

  removeMember(conversationId: string, userId: string): Observable<ChatConversationDetail> {
    return this.route(() => this.api.removeMember(conversationId, userId));
  }

  leaveGroup(conversationId: string): Observable<ChatLeaveResult> {
    return this.route(() => this.api.leaveGroup(conversationId));
  }

  copyGroup(conversationId: string, name?: string): Observable<ChatConversationDetail> {
    return this.route(() => this.api.copyGroup(conversationId, name));
  }

  deleteGroup(conversationId: string): Observable<unknown> {
    return this.route(() => this.api.deleteGroup(conversationId));
  }

  // ---- Presence --------------------------------------------------------------

  getPresence(): Observable<ChatPresenceEvent[]> {
    return this.route(() => this.api.getPresence());
  }

  setPresence(status: ChatManualStatus): Observable<ChatPresenceResult> {
    return this.route(() => this.api.setPresence(status));
  }

  getUnreadCount(): Observable<number> {
    return this.route(() => this.api.getUnreadCount());
  }

  // ---- Attachments -----------------------------------------------------------

  uploadAttachment(conversationId: string, file: File, durationMs?: number): Observable<ChatAttachment> {
    return this.route(() => this.api.uploadAttachment(conversationId, file, durationMs));
  }

  /** Where the download button points. */
  downloadUrl(attachment: ChatAttachment): string {
    return this.api.downloadUrl(attachment);
  }
}
